/**
 * PublicPostView — Full website experience for deep link visitors
 * Shows post in full Layout (sidebar + right sidebar + promo cards)
 * Interactions gated behind login, bottom bar nudges app download
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Home, Search, Shield, ShieldCheck, Smartphone, Eye, Users, Megaphone, FileCheck, MapPin, X } from 'lucide-react'
import Post from '../components/Post'
import Avatar from '../components/ui/Avatar'
import api from '../services/api'
import { MediaPreviewProvider } from '../contexts/MediaPreviewContext'
import { trackPostView, trackAppDownloadClick } from '../services/adTracking'
import MediaPreviewOverlay from './MediaPreview'

const PROMO_CARDS = [
  { icon: Shield, iconColor: '#1DA1F2', title: 'Trust what you see.', body: 'Every photo & video on OnRecord is stamped with time, location, and device proof. No edits. No fakes. Just truth.' },
  { icon: Eye, iconColor: '#F59E0B', title: "In the age of deepfakes, seeing isn't believing.", body: 'OnRecord is. Every post is cryptographically verified at capture. The world deserves proof, not pixels.' },
  { icon: Megaphone, iconColor: '#EF4444', title: 'Record what matters.', body: "Potholes, pollution, broken promises — put it on record. Your city won't fix what it can't see. Be the proof." },
  { icon: Users, iconColor: '#10B981', title: 'Inform, so no one can misinform.', body: "You're aware — now make people be-ware. Share verified ground reports that algorithms can't bury." },
  { icon: FileCheck, iconColor: '#8B5CF6', title: 'Your report. Your record. Your proof.', body: 'Journalists, activists, citizens — capture evidence that holds up. Timestamped. Geotagged. Tamper-proof.' },
  { icon: Smartphone, iconColor: '#EC4899', title: 'Take matters into your own hands.', body: 'Literally. Your phone is now a verified witness. Download OnRecord and turn everyday moments into undeniable evidence.' },
]

/** Normalize backend post format → Post component format */
function normalizePost(p: any) {
  const loc = p.location
  const address = loc
    ? (loc.address || `${loc.city || ''}${loc.state ? ', ' + loc.state : ''}`.trim() || undefined)
    : undefined
  return {
    id: p.id || p.postId,
    authorId: p.author?.id || p.userId,
    author: p.author || { name: 'Unknown', username: 'unknown' },
    content: p.text || p.content || '',
    mediaUrl: p.media?.url || p.mediaUri || p.mediaUrl,
    mediaType: p.media?.type === 'video' ? 'video' : p.media?.type === 'photo' ? 'image' : (p.type === 'video' ? 'video' : p.type === 'image' ? 'image' : p.mediaType),
    createdAt: p.created_at || p.createdAt || p.timestamp,
    location: address ? { address } : undefined,
    stats: p.engagement ? { likes: p.engagement.likes, comments: p.engagement.comments, reposts: p.engagement.reposts } : (p.stats || { likes: 0, comments: 0, reposts: 0 }),
    onRecordCode: p.onrecord_code || p.onRecordCode || p.postCode,
  }
}

export default function PublicPostView() {
  const { postId } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth()

  // If logged in, redirect to the authenticated post detail page
  if (!authLoading && isAuthenticated) {
    return <Navigate to={`/post/${postId}`} replace />
  }
  const [post, setPost] = useState<any>(null)
  const [morePosts, setMorePosts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showBottomBar, setShowBottomBar] = useState(false)

  useEffect(() => {
    async function loadData() {
      if (!postId) return
      try {
        setLoading(true)
        // Fetch the specific post (public, no auth)
        const result = await api.getPublicPost(postId)
        if (result.success && result.post) {
          setPost(result.post)
          trackPostView(postId!)
        }
        // Fetch a few more public posts for the feed below
        const publicPosts = await api.getPublicFeed(5)
        setMorePosts(publicPosts.filter((p: any) => (p.id || p.postId) !== postId).slice(0, 3))
      } catch (err) {
        console.error('Error loading post:', err)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [postId])

  // Show bottom bar after 2s
  useEffect(() => {
    const timer = setTimeout(() => setShowBottomBar(true), 2000)
    return () => clearTimeout(timer)
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-onrecord-orange"></div>
      </div>
    )
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-md px-4">
          <h1 className="text-3xl font-bold mb-3">Post not found</h1>
          <p className="text-gray-600 mb-6">This post may have been deleted or is not available.</p>
          <button onClick={() => navigate('/login')} className="bg-onrecord-orange text-white px-6 py-3 rounded-full font-bold hover:bg-orange-600 transition-colors">
            Go to OnRecord
          </button>
        </div>
      </div>
    )
  }

  return (
    <MediaPreviewProvider>
    <div className="flex flex-col min-h-screen bg-white">
      <div className="flex flex-1 max-w-7xl mx-auto w-full">

        {/* Left Sidebar — Guest version */}
        <div className="w-64 flex flex-col h-screen sticky top-0 px-4 py-2 border-r border-gray-200 hidden md:flex">
          <div className="mb-4 p-3 flex items-center gap-2">
            <img src="/logo.svg" alt="on record" className="w-7 h-7" />
            <img src="/onrecord-text-logo.png" alt="ON RECORD" className="h-4 opacity-50" />
          </div>

          <nav className="flex-1 space-y-1">
            <Link to="/login" className="flex items-center gap-4 px-4 py-3 rounded-full text-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium">
              <Home className="w-6 h-6" /> <span>Home</span>
            </Link>
            <Link to="/login" className="flex items-center gap-4 px-4 py-3 rounded-full text-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium">
              <Search className="w-6 h-6" /> <span>Explore</span>
            </Link>
          </nav>

          <div className="space-y-2 mb-4">
            <button onClick={() => navigate('/signup')} className="w-full font-bold text-[15px] py-3 rounded-full text-white" style={{ background: 'rgb(29, 155, 240)' }}>
              Sign up
            </button>
            <button onClick={() => navigate('/login')} className="w-full font-bold text-[15px] py-3 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors">
              Log in
            </button>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 min-w-0 border-r border-gray-200">
          {/* Mobile header */}
          <div className="md:hidden sticky top-0 bg-white border-b border-gray-200 z-40 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/logo.svg" alt="on record" className="w-7 h-7" />
              <span className="font-bold text-lg">on record</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/login')} className="px-3 py-1.5 rounded-full text-sm font-bold border border-gray-300">Log in</button>
              <button onClick={() => navigate('/signup')} className="px-3 py-1.5 rounded-full text-sm font-bold bg-onrecord-orange text-white">Sign up</button>
            </div>
          </div>

          {/* The post */}
          <Post post={normalizePost(post)} isGuest={true} />

          {/* More posts */}
          {morePosts.length > 0 && (
            <>
              <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="font-bold text-lg">More posts</h2>
              </div>
              {morePosts.map((p: any) => (
                <Post key={p.id || p.postId} post={normalizePost(p)} isGuest={true} />
              ))}
            </>
          )}

          {/* Sign up CTA */}
          <div className="px-4 py-12 text-center border-b border-gray-200">
            <h2 className="text-2xl font-bold mb-2">See what's real on OnRecord</h2>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">Join the community of citizen journalists sharing verified, authentic content.</p>
            <div className="flex flex-col gap-3 max-w-xs mx-auto">
              <button onClick={() => navigate('/signup')} className="w-full bg-onrecord-orange text-white px-6 py-3 rounded-full font-bold hover:bg-orange-600 transition-colors">
                Create account
              </button>
              <button onClick={() => navigate('/login')} className="w-full border border-gray-300 px-6 py-3 rounded-full font-bold hover:bg-gray-50 transition-colors">
                Log in
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 py-6">
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-400">
              <a href="/about" className="hover:underline">About</a>
              <a href="/terms" className="hover:underline">Terms</a>
              <a href="/privacy" className="hover:underline">Privacy</a>
            </div>
            <div className="mt-2 text-xs text-gray-400">© {new Date().getFullYear()} OnRecord</div>
          </div>
        </main>

        {/* Right Sidebar — always show promo for guests */}
        <div className="w-96 px-6 py-2 hidden lg:block h-screen sticky top-0 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
          {/* Rotating promo cards */}
          <div className="mt-2">
            <PromoCarousel />
          </div>

          {/* Why OnRecord */}
          <div className="rounded-2xl border border-gray-200 p-4 mb-4">
            <h3 className="font-bold text-lg text-gray-900 mb-3">Why OnRecord?</h3>
            <div className="space-y-3 text-[13px] text-gray-600">
              <div className="flex gap-2"><ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Every post is timestamped, geotagged, and tamper-proof</span></div>
              <div className="flex gap-2"><Eye className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" /><span>AI-powered authenticity detection for every upload</span></div>
              <div className="flex gap-2"><Users className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /><span>Built for journalists, activists, and everyday citizens</span></div>
              <div className="flex gap-2"><Megaphone className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" /><span>Your evidence, your voice — no algorithms to bury it</span></div>
            </div>
          </div>

          {/* Sign up nudge */}
          <div className="rounded-2xl border border-gray-200 p-4 mb-4 text-center">
            <p className="font-bold text-gray-900 mb-1">New to OnRecord?</p>
            <p className="text-[13px] text-gray-500 mb-3">Sign up to post, follow, and interact.</p>
            <button onClick={() => navigate('/signup')} className="w-full bg-onrecord-orange text-white py-2.5 rounded-full font-bold text-sm hover:bg-orange-600 transition-colors">
              Create account
            </button>
          </div>

          <div className="px-2 py-4">
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-400">
              <a href="/about" className="hover:underline">About</a>
              <a href="/terms" className="hover:underline">Terms</a>
              <a href="/privacy" className="hover:underline">Privacy</a>
            </div>
            <div className="mt-1 text-xs text-gray-400">© {new Date().getFullYear()} OnRecord</div>
          </div>
        </div>
      </div>

      {/* Bottom sticky bar — app download nudge */}
      {showBottomBar && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50" style={{ animation: 'slideUp 0.3s ease-out' }}>
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <img src="/logo.svg" alt="on record" className="w-8 h-8 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-bold text-sm truncate">Record what matters</p>
                <p className="text-gray-500 text-xs truncate">Download OnRecord — Record what matters.</p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => navigate('/login')} className="px-4 py-2 rounded-full font-bold border border-gray-300 text-sm hover:bg-gray-50">
                Log in
              </button>
              <button onClick={() => navigate('/signup')} className="px-4 py-2 rounded-full font-bold bg-onrecord-orange text-white text-sm hover:bg-orange-600">
                Sign up
              </button>
            </div>
            <button onClick={() => setShowBottomBar(false)} className="p-1.5 hover:bg-gray-100 rounded-full flex-shrink-0">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
      )}

      <MediaPreviewOverlay />

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
    </MediaPreviewProvider>
  )
}

/** Small promo carousel for right sidebar */
function PromoCarousel() {
  const [idx, setIdx] = useState(0)
  const [sliding, setSliding] = useState(false)
  const [nextIdx, setNextIdx] = useState<number | null>(null)

  useEffect(() => {
    const isLast = idx === PROMO_CARDS.length - 1
    const timer = setTimeout(() => {
      const next = (idx + 1) % PROMO_CARDS.length
      setNextIdx(next)
      setSliding(true)
      setTimeout(() => { setIdx(next); setNextIdx(null); setSliding(false) }, 500)
    }, isLast ? 12000 : 8000)
    return () => clearTimeout(timer)
  }, [idx])

  return (
    <div className="rounded-2xl border border-gray-200 p-4 mb-4 overflow-hidden relative" style={{ minHeight: '200px' }}>
      <div style={{ height: '155px', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          display: 'flex', width: '200%', gap: '2px',
          transform: sliding ? 'translateX(calc(-50% - 1px))' : 'translateX(0)',
          transition: sliding ? 'transform 0.55s ease-in-out' : 'none',
        }}>
          {[idx, nextIdx !== null ? nextIdx : (idx + 1) % PROMO_CARDS.length].map((i, pos) => {
            const c = PROMO_CARDS[i]
            return (
              <div key={pos} style={{ width: '50%', flexShrink: 0, borderLeft: pos === 1 ? '1px solid #e5e7eb' : 'none' }}>
                <div className="flex items-start gap-2 mb-2 pr-2">
                  <c.icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: c.iconColor }} />
                  <h3 className="font-bold text-[14px] text-gray-900 leading-tight">{c.title}</h3>
                </div>
                <p className="text-[12px] text-gray-600 leading-relaxed mb-3 line-clamp-3">{c.body}</p>
                <div className="flex items-center gap-2">
                  <a href="https://apps.apple.com/app/on-record/id1534718618" target="_blank" rel="noopener noreferrer" className="flex-1" onClick={() => trackAppDownloadClick('ios')}>
                    <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="App Store" className="h-8" />
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.onrecord.live" target="_blank" rel="noopener noreferrer" className="flex-1" onClick={() => trackAppDownloadClick('android')}>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Play Store" className="h-8" />
                  </a>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <div className="flex justify-center gap-1.5 mt-2">
        {PROMO_CARDS.map((_, i) => (
          <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: i === idx ? '#1DA1F2' : '#D1D5DB' }} />
        ))}
      </div>
    </div>
  )
}
